import React, { useCallback } from 'react';
import { Control, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ProjectedBalance, VacationBalances } from '~/data/absences';
import { AbsEnum } from '~/scenes/PeopleModule/components/Tabs/AbsencesTab/components/VacationBalanceData/useVacationBalanceData';
import { IFormInput } from '~/scenes/PeopleModule/components/Tabs/AbsencesTab/components/VacationBalanceData/useVacationBalanceDataForm';
import { SupplementalBalanceSummary } from '~/scenes/PeopleModule/components/Tabs/AbsencesTab/components/VacationBalanceData/VacationAndSupplementalBalances/SupplementalBalanceSummary';
import { VacationBalanceSummary } from '~/scenes/PeopleModule/components/Tabs/AbsencesTab/components/VacationBalanceData/VacationAndSupplementalBalances/VacationBalanceSummary';

type PropsType = {
  control: Control<IFormInput>;
  defaultData?: VacationBalances;
  editMode: boolean;

  bnEnableVacBank: boolean;
  vacBankType1: boolean;
  vacBankType2: boolean;
  vacBankType3: boolean;

  extraVacEnum: AbsEnum[];
  vacBankEnum: AbsEnum[];
};

export const VacationAndSupplementalBalances = (props: PropsType) => {
  const {
    control,
    defaultData,
    editMode,
    bnEnableVacBank,
    vacBankType1,
    vacBankType2,
    vacBankType3,
    extraVacEnum,
    vacBankEnum,
  } = props;

  const { t } = useTranslation();

  const getEnumNameByCode =
    (enums: AbsEnum[]) =>
    (code: string): string => {
      return enums.find((form) => form.code === code)?.name ?? '';
    };

  const vacBalanceEnumName = getEnumNameByCode(extraVacEnum);
  const vacBankEnumName = getEnumNameByCode(vacBankEnum);
  const isSummaryEnabled = (title: string) => !!title.trim();

  const summaryTitles = {
    extra: vacBalanceEnumName('EXTRA_BALANCE'),
    vacationBank1: vacBankEnumName('VACATION_BANK_1'),
    vacationBank2: vacBankEnumName('VACATION_BANK_2'),
    vacationBank3: vacBankEnumName('VACATION_BANK_3'),
  };

  const getBalanceCounts = useCallback(
    (balance?: number, projectedBalance?: ProjectedBalance) => {
      const currentBalance = balance || 0;
      const inProgress = projectedBalance?.fProjectedUnapproved || 0;
      const approved = projectedBalance?.fProjectedApproved || 0;

      return {
        balance: currentBalance,
        inProgress,
        approved,
        remaining: currentBalance - approved - inProgress,
      };
    },
    [],
  );

  return (
    <>
      <Controller
        name="fVacationBalance"
        control={control}
        render={({ field: { onChange, value } }) => {
          const counts = getBalanceCounts(
            defaultData?.fVacationBalance,
            defaultData?.fVacationBalanceProjDed,
          );

          return (
            <VacationBalanceSummary
              title={t('laVacBal')}
              {...counts}
              editMode={editMode}
              balanceFieldProps={{
                value,
                onChange,
              }}
              instruction={{
                title: t('laVacDaysLeft'),
                contentKey: 'INSTRUCTIONS_ABSENCE_VACBAL',
              }}
            />
          );
        }}
      />
      {bnEnableVacBank && (
        <>
          {!vacBankType1 && isSummaryEnabled(summaryTitles.vacationBank1) && (
            <Controller
              name="fVacationBalanceBank1"
              control={control}
              render={({ field: { onChange, value } }) => {
                const counts = getBalanceCounts(
                  defaultData?.fVacationBalanceBank1,
                  defaultData?.fVacationBalanceBank1ProjDed,
                );

                return (
                  <VacationBalanceSummary
                    title={vacBankEnumName('VACATION_BANK_1')}
                    {...counts}
                    editMode={editMode}
                    balanceFieldProps={{ value, onChange }}
                  />
                );
              }}
            />
          )}
          {!vacBankType2 && isSummaryEnabled(summaryTitles.vacationBank2) && (
            <Controller
              name="fVacationBalanceBank2"
              control={control}
              render={({ field: { onChange, value } }) => {
                const counts = getBalanceCounts(
                  defaultData?.fVacationBalanceBank2,
                  defaultData?.fVacationBalanceBank2ProjDed,
                );

                return (
                  <VacationBalanceSummary
                    title={vacBankEnumName('VACATION_BANK_2')}
                    {...counts}
                    editMode={editMode}
                    balanceFieldProps={{ value, onChange }}
                  />
                );
              }}
            />
          )}
          {!vacBankType3 && isSummaryEnabled(summaryTitles.vacationBank3) && (
            <Controller
              name="fVacationBalanceBank3"
              control={control}
              render={({ field: { onChange, value } }) => {
                const counts = getBalanceCounts(
                  defaultData?.fVacationBalanceBank3,
                  defaultData?.fVacationBalanceBank3ProjDed,
                );

                return (
                  <VacationBalanceSummary
                    title={vacBankEnumName('VACATION_BANK_3')}
                    {...counts}
                    editMode={editMode}
                    balanceFieldProps={{ value, onChange }}
                  />
                );
              }}
            />
          )}
        </>
      )}
      {!bnEnableVacBank && (
        <>
          {isSummaryEnabled(summaryTitles.extra) && (
            <Controller
              name="fVacationBalanceExtra"
              control={control}
              render={({ field: { onChange, value } }) => {
                const counts = getBalanceCounts(
                  defaultData?.fVacationBalanceExtra,
                  defaultData?.fVacationBalanceExtraProjDed,
                );

                return (
                  <VacationBalanceSummary
                    title={summaryTitles.extra}
                    {...counts}
                    editMode={editMode}
                    balanceFieldProps={{ value, onChange }}
                  />
                );
              }}
            />
          )}
          <SupplementalBalanceSummary
            control={control}
            totalFieldName={'fVacationBalanceSuppl1'}
            spentFieldName={'fVacationBalanceSuppl1Spent'}
            labels={{
              name: vacBalanceEnumName('SUPPLEMENTAL_1_NAME'),
              total: vacBalanceEnumName('SUPPLEMENTAL_1_TOTAL'),
              spent: vacBalanceEnumName('SUPPLEMENTAL_1_USED'),
            }}
            editMode={editMode}
          />
          <SupplementalBalanceSummary
            control={control}
            totalFieldName={'fVacationBalanceSuppl2'}
            spentFieldName={'fVacationBalanceSuppl2Spent'}
            labels={{
              name: vacBalanceEnumName('SUPPLEMENTAL_2_NAME'),
              total: vacBalanceEnumName('SUPPLEMENTAL_2_TOTAL'),
              spent: vacBalanceEnumName('SUPPLEMENTAL_2_USED'),
            }}
            editMode={editMode}
          />
        </>
      )}
    </>
  );
};
